import { useMemo } from "react";
import { COUNTRY_COORDS, FW_BUCKET_LABELS, resolveCountryCode, mapToChartData } from "./utils";

/**
 * Transforms the /api/home/stats response into chart-ready datasets.
 *
 * Every distribution map from the API is converted to:
 *   { chartData: [{ name, value }], total: number }
 * so the dashboard can render consistently with ChartCard + ChartDataTable.
 */

export default function useStatsTransform(statsData) {
  // ── Connectivity: onlineCount / offlineCount ──
  const connectivityData = useMemo(() => {
    if (!statsData) return null;
    const online = Number(statsData.onlineCount) || 0;
    const offline = Number(statsData.offlineCount) || 0;
    if (online === 0 && offline === 0) return null;
    return {
      online,
      offline,
      total: online + offline,
      chartData: [
        { name: "Online", value: online },
        { name: "Offline", value: offline },
      ],
    };
  }, [statsData]);

  // ── Fleet Health: connectorStatusTypeCounts.FAULTED, healthy = totalChargers − faulted ──
  const fleetHealthData = useMemo(() => {
    if (!statsData) return null;
    const totalChargers = Number(statsData.totalChargers) || 0;
    const faulted = Number(statsData.connectorStatusTypeCounts?.FAULTED) || 0;
    const healthy = Math.max(0, totalChargers - faulted);
    return {
      healthy,
      faulted,
      total: totalChargers,
      chartData: [
        { name: "Healthy", value: healthy },
        { name: "Faulted", value: faulted },
      ],
    };
  }, [statsData]);

  // ── Commissioning: commissionedCount / totalChargers ──
  const commissioningData = useMemo(() => {
    if (!statsData) return null;
    const total = Number(statsData.totalChargers) || 0;
    const commissioned = Number(statsData.commissionedCount) || 0;
    const pending = Math.max(0, total - commissioned);
    if (total === 0) return null;
    return {
      commissioned,
      pending,
      total,
      chartData: [
        { name: "Commissioned", value: commissioned },
        { name: "Not Commissioned", value: pending },
      ],
    };
  }, [statsData]);

  // ── Offline Duration: offlineOver24hCount vs rest of offline ──
  const offlineDurationData = useMemo(() => {
    if (!statsData) return null;
    const totalOffline = Number(statsData.offlineCount) || 0;
    const over24h = Number(statsData.offlineOver24hCount) || 0;
    const under24h = Math.max(0, totalOffline - over24h);
    if (totalOffline === 0) return null;
    return {
      total: totalOffline,
      chartData: [
        { name: "< 24 hr", value: under24h },
        { name: "> 24 hr", value: over24h },
      ],
    };
  }, [statsData]);

  // ── Country Counts: countryCounts → handles alpha-2 AND alpha-3 codes ──
  const countryData = useMemo(() => {
    if (!statsData?.countryCounts || typeof statsData.countryCounts !== "object") return null;
    const entries = Object.entries(statsData.countryCounts);
    if (entries.length === 0) return null;

    // Build counts map (keyed by resolved alpha-2 for display/map)
    const counts = {};
    // Keep original API keys for drill-down (grouped by resolved code)
    const originalKeys = {};
    entries.forEach(([code, count]) => {
      const a2 = resolveCountryCode(code);
      counts[a2] = (counts[a2] || 0) + (Number(count) || 0);
      // Store original key(s) — for drill-down we use the original API key
      if (!originalKeys[a2]) originalKeys[a2] = [];
      originalKeys[a2].push(code);
    });

    const chartData = Object.entries(counts)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([code, value]) => ({
        name: COUNTRY_COORDS[code]?.name || code,
        value,
        rawKey: code,
        // Original API keys for this country (may include "Germany" and "DE" both mapping to DE)
        apiKeys: originalKeys[code] || [code],
      }));

    const total = chartData.reduce((s, d) => s + d.value, 0);
    return { counts, chartData, total };
  }, [statsData]);

  // ── Firmware Freshness: fwUpdatedAtBuckets (lt_7d, 7d_to_30d, …) ──
  const fwFreshnessData = useMemo(() => {
    if (!statsData?.fwUpdatedAtBuckets) return null;
    return mapToChartData(statsData.fwUpdatedAtBuckets, k => FW_BUCKET_LABELS[k] || k);
  }, [statsData]);

  // ── Firmware Versions: fwVersionCounts ──
  const fwVersionsData = useMemo(() => {
    if (!statsData?.fwVersionCounts) return null;
    return mapToChartData(statsData.fwVersionCounts, k => `v${k}`);
  }, [statsData]);

  // ── Model Distribution: modelCounts ──
  const modelData = useMemo(() => {
    if (!statsData?.modelCounts) return null;
    return mapToChartData(statsData.modelCounts);
  }, [statsData]);

  // ── SKU Distribution: skuCounts ──
  const skuData = useMemo(() => {
    if (!statsData?.skuCounts) return null;
    return mapToChartData(statsData.skuCounts);
  }, [statsData]);

  // ── Phase Configuration: phaseCountCounts ("1", "3") ──
  const phaseConfigData = useMemo(() => {
    if (!statsData?.phaseCountCounts) return null;
    return mapToChartData(statsData.phaseCountCounts, k => `${k}-Phase`);
  }, [statsData]);

  // ── Max Current: maxCurrentCounts ("32.0", "40.0") → "32A", "40A" ──
  const maxCurrentData = useMemo(() => {
    if (!statsData?.maxCurrentCounts) return null;
    return mapToChartData(statsData.maxCurrentCounts, k => {
      const num = parseFloat(k);
      return Number.isFinite(num) ? `${Math.round(num)}A` : `${k}A`;
    });
  }, [statsData]);

  // ── DLB Adoption: dlbEnabledCounts ("1"=enabled, "0"=disabled) ──
  const dlbData = useMemo(() => {
    if (!statsData?.dlbEnabledCounts) return null;
    return mapToChartData(statsData.dlbEnabledCounts, k => (k === "1" ? "DLB Enabled" : "DLB Disabled"));
  }, [statsData]);

  // ── Partner Integration: partnerNameCounts ──
  const partnerData = useMemo(() => {
    if (!statsData?.partnerNameCounts) return null;
    return mapToChartData(statsData.partnerNameCounts);
  }, [statsData]);

  // ── Off-Grid Status: offGridCounts ("true"/"false") ──
  const offGridData = useMemo(() => {
    if (!statsData?.offGridCounts) return null;
    return mapToChartData(statsData.offGridCounts, k => (k === "true" ? "Off-Grid" : "On-Grid"));
  }, [statsData]);

  // ── Vendor Distribution: vendorNameCounts ──
  const vendorData = useMemo(() => {
    if (!statsData?.vendorNameCounts) return null;
    return mapToChartData(statsData.vendorNameCounts);
  }, [statsData]);

  // ── Charging Status: all statuses from connectorStatusTypeCounts shown as-is ──
  const chargingStatusData = useMemo(() => {
    if (!statsData?.connectorStatusTypeCounts || typeof statsData.connectorStatusTypeCounts !== "object") return null;
    const entries = Object.entries(statsData.connectorStatusTypeCounts);
    if (entries.length === 0) return null;

    // Pass every status through directly — use the raw API key (uppercased) as the
    // status identifier so StatusDonutChart can look up its color/label.
    const chartData = entries
      .map(([key, count]) => ({ status: key.toUpperCase(), count: Number(count) || 0 }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);

    if (chartData.length === 0) return null;
    return chartData;
  }, [statsData]);

  return {
    connectivityData,
    fleetHealthData,
    commissioningData,
    offlineDurationData,
    countryData,
    fwFreshnessData,
    fwVersionsData,
    modelData,
    skuData,
    phaseConfigData,
    maxCurrentData,
    dlbData,
    partnerData,
    offGridData,
    vendorData,
    chargingStatusData,
  };
}
